// src/store/modules/transcription.js
const state = {
  // 转写状态
  isTranscribing: false,
  transcriptionStatus: 'idle', // idle, starting, active, pausing, paused, stopping
  // 转写文本
  transcriptionText: [],
  // 开始时间
  startTime: null,
  // 时长（秒）
  duration: 0,
  // 定时器ID
  timerId: null,
  // 总字数
  wordCount: 0,
  // 发言人统计
  speakerStats: {}
}

const mutations = {
  SET_TRANSCRIBING_STATUS(state, status) {
    state.transcriptionStatus = status
    state.isTranscribing = status === 'active' || status === 'starting'
  },
  ADD_TRANSCRIPTION_TEXT(state, textChunk) {
    const newItem = {
      id: Date.now() + Math.random(),
      speaker: textChunk.speaker,
      text: textChunk.text,
      timestamp: textChunk.timestamp || new Date(),
      confidence: textChunk.confidence || 0.9
    }
    
    state.transcriptionText.push(newItem)
    
    // 更新字数统计
    state.wordCount += textChunk.text.length
    
    // 更新发言人统计
    if (!state.speakerStats[textChunk.speaker]) {
      state.speakerStats[textChunk.speaker] = {
        count: 0,
        words: 0
      }
    }
    state.speakerStats[textChunk.speaker].count++
    state.speakerStats[textChunk.speaker].words += textChunk.text.length
  },
  SET_START_TIME(state, time) {
    state.startTime = time
  },
  UPDATE_DURATION(state) {
    if (state.startTime) {
      state.duration = Math.floor((Date.now() - state.startTime) / 1000)
    }
  },
  CLEAR_TRANSCRIPTION(state) {
    state.transcriptionText = []
    state.wordCount = 0
    state.speakerStats = {}
    state.duration = 0
    state.startTime = null
    
    if (state.timerId) {
      clearInterval(state.timerId)
      state.timerId = null
    }
  },
  SET_TIMER_ID(state, timerId) {
    state.timerId = timerId
  }
}

const actions = {
  // 开启转写
  async startTranscription({ commit, state, rootState }) {
    if (state.transcriptionStatus === 'active') return
    
    commit('SET_TRANSCRIBING_STATUS', 'starting')
    
    try {
      const meetingId = rootState.meeting.currentMeeting?.meetingId
      if (!meetingId) {
        throw new Error('会议ID不存在')
      }
      
      const response = await this._vm.$api.transcription.start(meetingId)
      
      commit('SET_TRANSCRIBING_STATUS', 'active')
      commit('SET_START_TIME', new Date())
      
      // 开启定时器更新时长
      const timerId = setInterval(() => {
        commit('UPDATE_DURATION')
      }, 1000)
      
      commit('SET_TIMER_ID', timerId)
      
      return response
    } catch (error) {
      commit('SET_TRANSCRIBING_STATUS', 'idle')
      console.error('开启转写失败:', error)
      throw error
    }
  },
  
  // 暂停转写
  async pauseTranscription({ commit, state }) {
    if (state.transcriptionStatus !== 'active') return
    
    commit('SET_TRANSCRIBING_STATUS', 'pausing')
    
    try {
      await this._vm.$api.transcription.pause()
      commit('SET_TRANSCRIBING_STATUS', 'paused')
      
      // 暂停定时器
      if (state.timerId) {
        clearInterval(state.timerId)
        commit('SET_TIMER_ID', null)
      }
    } catch (error) {
      commit('SET_TRANSCRIBING_STATUS', 'active')
      console.error('暂停转写失败:', error)
      throw error
    }
  },
  
  // 恢复转写
  async resumeTranscription({ commit, state }) {
    if (state.transcriptionStatus !== 'paused') return
    
    commit('SET_TRANSCRIBING_STATUS', 'active')
    commit('SET_START_TIME', new Date())
    
    // 重新开启定时器
    const timerId = setInterval(() => {
      commit('UPDATE_DURATION')
    }, 1000)
    
    commit('SET_TIMER_ID', timerId)
    
    try {
      await this._vm.$api.transcription.resume()
    } catch (error) {
      commit('SET_TRANSCRIBING_STATUS', 'paused')
      console.error('恢复转写失败:', error)
      throw error
    }
  },
  
  // 停止转写
  async stopTranscription({ commit, state, dispatch }) {
    commit('SET_TRANSCRIBING_STATUS', 'stopping')
    
    try {
      const response = await this._vm.$api.transcription.stop()
      
      commit('SET_TRANSCRIBING_STATUS', 'idle')
      
      // 清除定时器
      if (state.timerId) {
        clearInterval(state.timerId)
        commit('SET_TIMER_ID', null)
      }
      
      // 通知总结模块可以生成总结
      dispatch('summary/saveTranscription', state.transcriptionText, { root: true })
      
      return response
    } catch (error) {
      commit('SET_TRANSCRIBING_STATUS', 'active')
      console.error('停止转写失败:', error)
      throw error
    }
  },
  
  // 接收实时转写数据
  receiveTranscriptionData({ commit }, data) {
    if (data && data.text) {
      commit('ADD_TRANSCRIPTION_TEXT', {
        speaker: data.speaker || '未知发言人',
        text: data.text,
        timestamp: data.timestamp || new Date()
      })
    }
  },
  
  // 清除转写数据
  clearTranscription({ commit }) {
    commit('CLEAR_TRANSCRIPTION')
  },
  
  // 获取转写统计
  getTranscriptionStats({ state }) {
    return {
      duration: state.duration,
      wordCount: state.wordCount,
      speakerStats: state.speakerStats,
      lineCount: state.transcriptionText.length
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}